import { useMemo, useState } from 'react';
import {
  Badge,
  Balance,
  Button,
  Callout,
  Card,
  Chip,
  Combobox,
  DataGrid,
  Field,
  Input,
  Money,
  PageHeader,
  Toolbar,
  TotalRow,
  addDecimal,
  cn,
  formatBalance,
  formatDate,
  formatMoney,
  subDecimal,
  type ColumnDef,
  type ComboboxOption,
} from '@biovet/ui';
import { useResource, type ApiAccount, type LedgerResponse, type LedgerRow } from '../lib/api.js';
import { exportReport } from '../lib/export.js';
import { printReport, type ReportTable } from '../lib/print.js';
import { baseCurrency, companyToday, monthOf, shiftMonth } from '../lib/reference-data.js';
import { VoucherDetailModal } from './VoucherDetailModal.js';

interface Row extends LedgerRow {
  running: string;
}

export function GeneralLedgerScreen({
  accountCode,
  onAccountChange,
}: {
  accountCode: string;
  onAccountChange: (code: string) => void;
}) {
  const [range, setRange] = useState(() => monthOf(companyToday()));
  const { from, to } = range;
  const setFrom = (value: string) => setRange((r) => ({ ...r, from: value }));
  const setTo = (value: string) => setRange((r) => ({ ...r, to: value }));
  const [openVoucher, setOpenVoucher] = useState<{ type: string; no: string } | null>(null);

  const accountList = useResource<{ accounts: ApiAccount[] }>('/accounts');
  const ledger = useResource<LedgerResponse>('/general-ledger', {
    account: accountCode,
    from: from || undefined,
    to: to || undefined,
  });

  const accountOptions = useMemo<ComboboxOption[]>(
    () =>
      (accountList.data?.accounts ?? [])
        .filter((a) => a.isLeaf)
        .map((a) => ({
          value: a.code,
          label: `${a.code} · ${a.name}`,
          hint: a.parentCode ? `under ${a.parentCode}` : undefined,
          group: a.type.toLowerCase(),
        })),
    [accountList.data],
  );

  const { rows, totals } = useMemo(() => {
    const source = ledger.data?.rows ?? [];
    let running = ledger.data?.opening ?? '0';
    let debit = '0';
    let credit = '0';

    const computed: Row[] = source.map((row) => {
      running = addDecimal(running, row.signedBase);
      debit = addDecimal(debit, row.debitBase);
      credit = addDecimal(credit, row.creditBase);
      return { ...row, running };
    });

    return {
      rows: computed,
      totals: { debit, credit, closing: running, movement: subDecimal(debit, credit) },
    };
  }, [ledger.data]);

  const columns = useMemo<ColumnDef<Row, unknown>[]>(
    () => [
      {
        id: 'date',
        header: 'Date',
        accessorKey: 'date',
        cell: (c) => formatDate(c.getValue<string>()),
        meta: { width: '104px', numeric: true },
      },
      {
        id: 'voucher',
        header: 'Voucher',
        accessorKey: 'voucherNo',
        cell: (c) => (
          <button
            type="button"
            onClick={() =>
              setOpenVoucher({
                type: c.row.original.voucherType,
                no: c.getValue<string>(),
              })
            }
            title="Show the complete entry"
            className={cn(
              'bb-mono cursor-pointer border-0 bg-transparent p-0 text-[12px]',
              'text-[var(--accent)] hover:underline',
            )}
          >
            {c.getValue<string>()}
          </button>
        ),
        meta: { width: '150px' },
      },
      {
        id: 'particulars',
        header: 'Particulars',
        accessorKey: 'particulars',
        cell: (c) => (
          <span className="flex items-center gap-2">
            <span className="truncate">{c.getValue<string>()}</span>
            {c.row.original.partyName && (
              <span className="shrink-0 text-[11px] text-[var(--ink-3)]">
                {c.row.original.partyName}
              </span>
            )}
            {c.row.original.status !== 'POSTED' && (
              <Badge status={c.row.original.status} className="shrink-0" />
            )}
          </span>
        ),
      },
      {
        id: 'debit',
        header: 'Debit',
        accessorKey: 'debitBase',
        cell: (c) => <Money value={c.getValue<string>()} dashOnZero />,
        meta: { align: 'right', numeric: true, width: '150px' },
      },
      {
        id: 'credit',
        header: 'Credit',
        accessorKey: 'creditBase',
        cell: (c) => <Money value={c.getValue<string>()} dashOnZero />,
        meta: { align: 'right', numeric: true, width: '150px' },
      },
      {
        id: 'balance',
        header: 'Balance',
        accessorKey: 'running',
        cell: (c) => <Balance value={c.getValue<string>()} />,
        meta: { align: 'right', numeric: true, width: '164px' },
      },
    ],
    [],
  );

  const account = ledger.data?.account;

  const report = (): ReportTable => ({
    title: account ? `General ledger — ${account.code} ${account.name}` : 'General ledger',
    fileName: account ? `ledger-${account.code}` : 'general-ledger',
    meta: `${from || to ? `${from || 'start'} to ${to || 'latest'}` : 'Whole book'} · ${baseCurrency()} · ${rows.length} lines`,
    columns: [
      { label: 'Date', width: '84px' },
      { label: 'Voucher', width: '116px' },
      { label: 'Particulars' },
      { label: 'Debit', align: 'right', width: '104px' },
      { label: 'Credit', align: 'right', width: '104px' },
      { label: 'Balance', align: 'right', width: '116px' },
    ],
    rows: [
      {
        cells: ['', '', 'Opening balance', '', '', formatBalance(ledger.data?.opening ?? '0')],
        head: true,
      },
      ...rows.map((row) => [
        formatDate(row.date),
        row.voucherNo,
        [row.particulars, row.partyName].filter(Boolean).join(' · '),
        formatMoney(row.debitBase, { dashOnZero: true }),
        formatMoney(row.creditBase, { dashOnZero: true }),
        formatBalance(row.running),
      ]),
      {
        cells: [
          '',
          '',
          'Closing balance',
          formatMoney(totals.debit),
          formatMoney(totals.credit),
          formatBalance(totals.closing),
        ],
        total: true,
      },
    ],
  });

  return (
    <>
      <PageHeader
        breadcrumb="Finance & Accounts / General Ledger"
        title={account ? `General ledger — ${account.code} ${account.name}` : 'General ledger'}
        description="Every posted entry against one account, with a running balance."
        actions={
          <>
            <Button variant="secondary" size="sm" onClick={() => printReport(report())}>
              Print
            </Button>
            <Button variant="secondary" size="sm" onClick={() => exportReport(report())}>
              Export XLSX
            </Button>
          </>
        }
        meta={
          <>
            <Chip>{from || to ? `${from || 'start'} → ${to || 'latest'}` : 'Whole book'}</Chip>
            <Chip>PKR</Chip>
            {account?.isControl && <Badge tone="info">control account</Badge>}
            <Chip>{rows.length} movements</Chip>
          </>
        }
      />

      {ledger.error && (
        <div className="pb-3.5">
          <Callout tone="danger" title="Could not load the ledger">
            {ledger.error}
          </Callout>
        </div>
      )}

      <Card flush>
        <Toolbar>
          <Field label="Account" className="min-w-[280px] flex-1">
            <Combobox
              variant="field"
              value={accountCode}
              onChange={onAccountChange}
              options={accountOptions}
              placeholder="Search code or name…"
              emptyText="No account matches"
              aria-label="Account"
            />
          </Field>
          <Field label="From">
            <Input
              size="sm"
              type="date"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
              aria-label="From date"
            />
          </Field>
          <Field label="To">
            <Input
              size="sm"
              type="date"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              aria-label="To date"
            />
          </Field>
          {}
          <Button
            size="sm"
            variant="ghost"
            title="Previous month"
            onClick={() => setRange((r) => shiftMonth(r.from || companyToday(), -1))}
          >
            ‹
          </Button>
          <Button
            size="sm"
            variant="ghost"
            title="Next month"
            onClick={() => setRange((r) => shiftMonth(r.from || companyToday(), 1))}
          >
            ›
          </Button>
          <Button size="sm" variant="ghost" onClick={() => setRange(monthOf(companyToday()))}>
            This month
          </Button>
          {(from || to) && (
            <Button size="sm" variant="ghost" onClick={() => setRange({ from: '', to: '' })}>
              Whole book
            </Button>
          )}
        </Toolbar>

        <div className="flex items-center gap-3 border-b border-[var(--line-2)] px-[var(--pad)] py-2.5">
          <span className="text-[12px] text-[var(--ink-3)]">Opening balance</span>
          <Balance value={ledger.data?.opening ?? '0'} className="text-[12.5px]" />
          <div className="flex-1" />
          <span className="text-[12px] text-[var(--ink-3)]">
            {formatMoney(totals.debit)} Dr · {formatMoney(totals.credit)} Cr in the window
          </span>
        </div>

        <DataGrid
          aria-label="General ledger"
          data={rows}
          columns={columns}
          loading={ledger.loading}
          virtualize={rows.length > 200}
          emptyTitle="Nothing posted to this account"
          emptyDescription="No posted line falls in this window. Widen the dates, or pick another account."
          footer={
            <TotalRow
              cells={[
                { content: '', width: '104px' },
                { content: '', width: '150px' },
                { content: 'Closing balance' },
                {
                  content: <Money value={totals.debit} />,
                  align: 'right',
                  numeric: true,
                  width: '150px',
                },
                {
                  content: <Money value={totals.credit} />,
                  align: 'right',
                  numeric: true,
                  width: '150px',
                },
                {
                  content: <Balance value={totals.closing} />,
                  align: 'right',
                  numeric: true,
                  width: '164px',
                },
              ]}
            />
          }
        />
      </Card>

      <p data-report-foot="1" className="hidden pt-4 text-[11px] text-[var(--ink-3)]">
        System-generated from biovet Accounting. Figures in PKR unless stated otherwise.
      </p>

      <VoucherDetailModal
        voucherType={openVoucher?.type ?? null}
        voucherNo={openVoucher?.no ?? null}
        onClose={() => setOpenVoucher(null)}
        onOpenLedger={(code) => {
          setOpenVoucher(null);
          onAccountChange(code);
        }}
      />
    </>
  );
}
